import { useState } from 'react';
import { useFetchCallback } from '@services/services';
import toast from 'react-hot-toast';

interface RespondPayload {
  option_id: string;
  option_value?: string;
}

export type DismissPayload = { dismissed: true } | { cta_clicked: true };

const persistDismissLocally = (announcementId: string) => {
  const key = 'plick_dismissed_show_once';
  const list: string[] = JSON.parse(localStorage.getItem(key) ?? '[]');
  if (!list.includes(announcementId)) {
    localStorage.setItem(key, JSON.stringify([...list, announcementId]));
  }
};

interface RespondResponse {
  message: string;
  response: {
    id: string;
    announcement_id: string;
    commerce_id: string;
    option_id: string;
    option_value: string;
    responded_at: string;
  };
  option: {
    label: string;
    value: string;
  };
}

export const useRespondAnnouncement = () => {
  const [submittingOptionId, setSubmittingOptionId] = useState<string | null>(null);

  const { call } = useFetchCallback<RespondPayload, RespondResponse>('post', '/v1/announcement');

  const dismiss = async (announcementId: string, payload: DismissPayload) => {
    persistDismissLocally(announcementId);
    try {
      await call({
        path: `${announcementId}/respond`,
        data: payload as unknown as RespondPayload & Record<string, unknown>,
      });
    } catch {
      // localStorage ya protege aunque el backend falle
    }
  };

  const respond = async (announcementId: string, optionId: string, optionValue?: string) => {
    setSubmittingOptionId(optionId);
    try {
      const payload: RespondPayload = { option_id: optionId };
      if (optionValue) payload.option_value = optionValue;

      const response = await call({
        path: `${announcementId}/respond`,
        data: payload as RespondPayload & Record<string, unknown>,
      });
      return response;
    } catch {
      toast.error('Error al registrar tu respuesta');
      return null;
    } finally {
      setSubmittingOptionId(null);
    }
  };

  return { respond, dismiss, isSubmitting: submittingOptionId !== null, submittingOptionId };
};
